import { StorageService } from "./LocalStorageService";
import { showBookPromoModal } from "../utils/modal/ModalManager";
import ApiService from "../handlers/apiService";
import { SpecialDealsService } from "./SpecialDealsService";

/**
 * Service responsible for gating and showing the Book Promo modal.
 *
 * Rules:
 * - Same relation to onboarding/first-time user as Special Deals (i.e., not shown on the first-ever session when onboarding runs)
 * - Show only if there are limited books that are currently available
 * - Show once per day
 * - If both Book Promo and Special Deals are eligible, Book Promo shows first, then Special Deals after closing
 */
export class BookPromoService {
  private static hasCheckedThisSession = false;

  static async checkAndShowBookPromo(): Promise<void> {
    if (this.hasCheckedThisSession) return;
    this.hasCheckedThisSession = true;

    try {
      // Do not show if already shown today
      if (StorageService.hasBookPromoBeenShownToday()) {
        SpecialDealsService.checkAndShowSpecialDeals();
        return;
      }

      // Same relation to onboarding as Special Deals: skip on true first-time (no last visit)
      const lastVisit = StorageService.getLastVisitTime();
      if (!lastVisit) {
        SpecialDealsService.checkAndShowSpecialDeals();
        return;
      }

      // Fetch books from API and filter for limited + available
      const api = ApiService.getInstance();
      const response = await api.api.booksList();

      if (response?.books) {
        const limitedAvailableBooks = response.books.filter(
          (book) => book.isLimited && book.isAvailable && !book.isOwned
        );

        if (limitedAvailableBooks.length > 0) {
          // Eligible → show Book Promo first
          showBookPromoModal();
          // Then trigger Special Deals check which will queue it (ModalManager will handle order)
          SpecialDealsService.checkAndShowSpecialDeals();
        } else {
          SpecialDealsService.checkAndShowSpecialDeals();
        }
      } else {
        SpecialDealsService.checkAndShowSpecialDeals();
      }
    } catch (error) {
      console.error("BookPromoService: failed to check/show promo", error);
      try {
        SpecialDealsService.checkAndShowSpecialDeals();
      } catch {}
    }
  }

  /** Reset the session check flag (useful for testing) */
  static resetSessionCheck(): void {
    this.hasCheckedThisSession = false;
  }
}

export default BookPromoService;
